import type {
  PlannerBenchPlayer,
  PlannerGameweek,
  PlannerSquadPlayer
} from '@api-service/planner';
import type { LineupPlayer } from '@components/team-lineup';
import { resolveChipLabel } from '@lib/my-team/utils/chip-strategy';

const toLineupPlayer = (
  player: PlannerSquadPlayer | PlannerBenchPlayer,
  gameweek: number
): LineupPlayer => ({
  id: player.id,
  displayName: player.displayName,
  position: player.position,
  team: { shortName: player.team.shortName },
  kitImageUrl: player.kitImageUrl,
  // What a reader would pay for the player, not the contributor's own sell
  // value, which the Pick page shows.
  price: player.price,
  // The box shows one gameweek's team, so only that gameweek's fixtures: one
  // tile, two in a double gameweek, none in a blank one.
  fixtures: player.fixtures
    .filter((fixture) => fixture.gameweek === gameweek)
    .map((fixture) => ({
      gameweek: fixture.gameweek,
      opponent: { shortName: fixture.opponent.shortName },
      isHome: fixture.isHome,
      predictedPoints: fixture.predictedPoints
    })),
  isCaptain: player.isCaptain,
  isViceCaptain: player.isViceCaptain,
  status: player.status,
  chanceOfPlaying: player.chanceOfPlaying
});

/** The planner already lists each half in slot order. */
export const buildPlannedTeamLineups = (
  gameweek: PlannerGameweek
): { starting: LineupPlayer[]; bench: LineupPlayer[] } => ({
  starting: gameweek.starting.map((player) =>
    toLineupPlayer(player, gameweek.gameweek)
  ),
  bench: gameweek.bench.map((player) =>
    toLineupPlayer(player, gameweek.gameweek)
  )
});

/** e.g. "Wildcard · 2 transfers · £0.5m in the bank" */
export const describePlannedTeam = (gameweek: PlannerGameweek): string => {
  const { made } = gameweek.transfers;
  return [
    gameweek.activeChip ? resolveChipLabel(gameweek.activeChip) : undefined,
    made > 0 ? `${made} ${made === 1 ? 'transfer' : 'transfers'}` : undefined,
    `£${gameweek.bank.toFixed(1)}m in the bank`
  ]
    .filter(Boolean)
    .join(' · ');
};
